import React, { useMemo } from 'react';
import { AlertOctagon, Building2, FolderCheck, Layers, ShieldCheck, Server } from 'lucide-react';
import { ControlInventoryTable } from './ControlInventoryTable.tsx';
import { WorkpapersPackageView } from './WorkpapersPackageView.tsx';
import { ExceptionReportTable } from './ExceptionReportTable.tsx';
import { IssueLedgerTable } from './IssueLedgerTable.tsx';
import { RelianceEligibleTable } from './RelianceEligibleTable.tsx';
import { ProcessChartingStatusTable } from './ProcessChartingStatusTable.tsx';
import { ScopeTestingStatusChart } from '../scope/ScopeTestingStatusChart.tsx';
import { ScopePlannedCompletedChart } from '../scope/ScopePlannedCompletedChart.tsx';
import { ScopeMaterialityTable } from '../scope/ScopeMaterialityTable.tsx';
import { PageHeader, SectionHeader, StatCard, WidgetCard } from '../controller/shared.tsx';
import {
  useAuditorControlInventory,
  useAuditorExceptionReport,
  useAuditorIssueLedger,
  useAuditorRelianceEligible,
} from '../../../hooks/analytics/useAuditorReports.ts';
import { useCfoCoverageHeatmap, useCfoSOCCoverage } from '../../../hooks/analytics/useCfoReports.ts';
import {
  useScopeEntities,
  useScopeRiskSummary,
  useScopeSystems,
} from '../../../hooks/analytics/useScopeReports.ts';
import { AnalyticsSlicerBar, type SlicerDef } from '../AnalyticsSlicerBar.tsx';
import { AnalyticsTabs } from '../AnalyticsTabs.tsx';
import { KpiRow, type KpiItem } from '../KpiRow.tsx';
import { applySlicers, deriveSlicerOptions, useSlicerState } from '../slicerUtils.ts';

const SLICER_KEYS = ['status', 'control_type', 'significance'];

/**
 * External Auditor analytics dashboard (Feature 5.3, reports #37-#41). A
 * read-only view for external auditors reviewing reliance-eligible controls
 * — no edit actions anywhere on this page.
 *
 * IA: PageHeader -> slicer bar -> always-visible KPI row -> tabbed body. The
 * slicer bar and KPI row are driven off the Control Inventory for Reliance
 * rows (report #37 — already `external_auditor_reliance = true` controls),
 * with Status/Control Type/Significance slicers. The resulting control-id
 * set filters the Exception Report; the Reliance-Eligible scoping table
 * (report #41, a deliberately broader set than the inventory) is filtered by
 * its own Control Type/Significance columns instead of the shared control-id
 * set, so slicing doesn't collapse it down to only-already-relied-upon
 * controls. The Issue Ledger (issue-level, no control_id) and the Workpapers
 * Package (single-control picker) don't share a joinable column and are left
 * independent — noted in the digest. "Systems w/ SOC" reuses the existing
 * CFO SOC Coverage hook (no new endpoint) since Auditor has no SOC report of
 * its own.
 */
export const ExternalAuditorDashboard: React.FC = () => {
  const { data: inventoryData } = useAuditorControlInventory();
  const inventoryRows = useMemo(() => inventoryData ?? [], [inventoryData]);
  const { data: exceptionData } = useAuditorExceptionReport();
  const { data: relianceEligibleData } = useAuditorRelianceEligible();
  const { data: issueLedgerData } = useAuditorIssueLedger();
  const { data: socData } = useCfoSOCCoverage();

  // Audit Scope Summary section (feedback round 2026-08) — SOX cycles count,
  // risks mapped/unmapped, systems, entities, materiality.
  const { data: coverageHeatmapData } = useCfoCoverageHeatmap();
  const { data: riskSummaryData } = useScopeRiskSummary();
  const { data: systemsData } = useScopeSystems();
  const { data: entitiesData } = useScopeEntities();

  const slicers = useSlicerState(SLICER_KEYS);

  const slicerDefs = useMemo<SlicerDef[]>(
    () => [
      { key: 'status', label: 'Status', options: deriveSlicerOptions(inventoryRows, (r) => r.status) },
      { key: 'control_type', label: 'Control Type', options: deriveSlicerOptions(inventoryRows, (r) => r.control_type) },
      { key: 'significance', label: 'Significance', options: deriveSlicerOptions(inventoryRows, (r) => r.significance) },
    ],
    [inventoryRows]
  );

  const filteredInventoryRows = useMemo(
    () =>
      applySlicers(inventoryRows, slicers.values, {
        status: (r) => r.status,
        control_type: (r) => r.control_type,
        significance: (r) => r.significance,
      }),
    [inventoryRows, slicers.values]
  );

  const controlIdFilter = useMemo(
    () => new Set(filteredInventoryRows.map((r) => r.control_id)),
    [filteredInventoryRows]
  );

  const kpis = useMemo<KpiItem[]>(() => {
    const exceptions = (exceptionData ?? []).filter((r) => controlIdFilter.has(r.control_id)).length;
    const relianceEligible = (relianceEligibleData ?? []).length;
    const openIssues = (issueLedgerData ?? []).filter((r) => (r.status ?? '').toLowerCase() !== 'closed').length;
    const systemsWithSOC = (socData ?? []).filter(
      (r) => r.soc1_type1 || r.soc1_type2 || r.soc2_type1 || r.soc2_type2
    ).length;

    return [
      {
        key: 'reliance_controls',
        title: 'Reliance Controls',
        value: filteredInventoryRows.length,
        icon: <ShieldCheck className="h-5 w-5" />,
        color: 'bg-blue-100 text-blue-600',
      },
      {
        key: 'exceptions',
        title: 'Exceptions',
        value: exceptions,
        icon: <AlertOctagon className="h-5 w-5" />,
        color: 'bg-rose-100 text-rose-600',
      },
      {
        key: 'open_issues',
        title: 'Open Issues',
        value: openIssues,
        icon: <FolderCheck className="h-5 w-5" />,
        color: 'bg-amber-100 text-amber-600',
      },
      {
        key: 'reliance_eligible',
        title: 'Reliance-Eligible',
        value: relianceEligible,
        icon: <Building2 className="h-5 w-5" />,
        color: 'bg-purple-100 text-purple-600',
      },
      {
        key: 'systems_soc',
        title: 'Systems w/ SOC',
        value: systemsWithSOC,
        icon: <Server className="h-5 w-5" />,
        color: 'bg-slate-100 text-slate-600',
      },
    ];
  }, [filteredInventoryRows, exceptionData, relianceEligibleData, issueLedgerData, socData, controlIdFilter]);

  return (
    <div className="min-h-screen bg-slate-50 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader
          icon={<ShieldCheck className="h-5 w-5" />}
          iconClassName="bg-slate-100 text-slate-600"
          title="External Auditor View"
          description="Read-only reliance scoping, workpapers, exceptions, and issue tracking"
        />

        <AnalyticsSlicerBar
          slicers={slicerDefs}
          values={slicers.values}
          onChange={slicers.setValue}
          onClear={slicers.clear}
          activeCount={slicers.activeCount}
        />

        <KpiRow items={kpis} />

        <AnalyticsTabs
          tabs={[
            {
              key: 'overview',
              label: 'Overview',
              content: (
                <div className="grid grid-cols-1 gap-6">
                  <section>
                    <SectionHeader title="Exception Report" />
                    <ExceptionReportTable controlIdFilter={controlIdFilter} />
                  </section>
                  <section>
                    <SectionHeader title="Reliance-Eligible Controls (Scoping Aid)" />
                    <RelianceEligibleTable
                      typeFilter={slicers.values.control_type || null}
                      significanceFilter={slicers.values.significance || null}
                    />
                  </section>
                </div>
              ),
            },
            {
              key: 'workpapers',
              label: 'Workpapers',
              content: <WorkpapersPackageView />,
            },
            {
              key: 'scope',
              label: 'Scope & Coverage',
              content: (
                <div className="grid grid-cols-1 gap-6">
                  <section>
                    <SectionHeader title="Testing Status" description="Current test runs by workflow status, plus planned-vs-completed progress" />
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <ScopeTestingStatusChart />
                      <ScopePlannedCompletedChart />
                    </div>
                  </section>
                  <section>
                    <SectionHeader title="Process Charting Status" description="Charted vs. uncharted processes, by SOX cycle" />
                    <ProcessChartingStatusTable />
                  </section>
                  <section>
                    <SectionHeader title="Audit Scope Summary" description="SOX cycles, risk coverage, systems, entities, and materiality" />
                    <WidgetCard title="Audit Scope Summary">
                      <div className="analytics-kpi-grid gap-3 mb-4">
                        <StatCard
                          title="SOX Cycles"
                          value={coverageHeatmapData?.length ?? 0}
                          icon={<Layers className="h-5 w-5" />}
                          color="bg-blue-100 text-blue-600"
                        />
                        <StatCard
                          title="Risks Mapped"
                          value={riskSummaryData ? `${riskSummaryData.mapped_count}/${riskSummaryData.total}` : '—'}
                          color="bg-emerald-100 text-emerald-600"
                          subtitle={`${riskSummaryData?.unmapped_count ?? 0} unmapped`}
                        />
                        <StatCard title="Systems In Scope" value={systemsData?.total ?? 0} icon={<Server className="h-5 w-5" />} color="bg-cyan-100 text-cyan-600" />
                        <StatCard title="Entities In Scope" value={entitiesData?.total ?? 0} icon={<Building2 className="h-5 w-5" />} color="bg-indigo-100 text-indigo-600" />
                      </div>
                      <ScopeMaterialityTable />
                    </WidgetCard>
                  </section>
                </div>
              ),
            },
            {
              key: 'raw',
              label: 'Raw Data',
              content: (
                <div className="grid grid-cols-1 gap-6">
                  <section>
                    <SectionHeader title="Control Inventory for Reliance" />
                    <ControlInventoryTable rows={filteredInventoryRows} />
                  </section>
                  <section>
                    <SectionHeader title="Issue Ledger" />
                    <IssueLedgerTable />
                  </section>
                </div>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
};

export default ExternalAuditorDashboard;
